import {Box} from '@mui/material';
import React, {useMemo, useState} from 'react';

import {CompareEvaluationsControlBar} from '../components/CompareEvaluationsControlBar';
import {
  useAvailableDatasets,
  useSelectedModelsCount,
} from '../hooks/useLeaderboardComparison';
import {useLeaderboardNavigation} from '../hooks/useLeaderboardNavigation';
import {LeaderboardGrid} from '../views/Leaderboard/LeaderboardGrid';
import {useLeaderboardData} from '../views/Leaderboard/query/hookAdapters';
import {getAllEvaluationRecords} from './LeaderboardPage/LeaderboardPage';

type EvaluationLeaderboardTabProps = {
  entity: string;
  project: string;
  evaluationObjectName: string;
  evaluationObjectVersion: string;
};

export const EvaluationLeaderboardTab: React.FC<
  EvaluationLeaderboardTabProps
> = props => {
  const {entity, project, evaluationObjectName, evaluationObjectVersion} =
    props;

  const [selectedEvaluations, setSelectedEvaluations] = useState<string[]>([]);

  const {loading, data} = useLeaderboardData(entity, project, {
    sourceEvaluations: [
      {
        name: evaluationObjectName,
        version: evaluationObjectVersion,
      },
    ],
  });

  // Compute all records from grouped data
  const allRecords = useMemo(() => {
    if (!data) {
      return [];
    }
    return getAllEvaluationRecords(data);
  }, [data]);

  // Calculate the number of selected models (rows) based on selectedEvaluations
  const selectedModelsCount = useSelectedModelsCount(
    data,
    allRecords,
    selectedEvaluations
  );

  // Calculate available datasets based on selected evaluations
  const availableDatasets = useAvailableDatasets(
    data,
    allRecords,
    selectedEvaluations
  );

  // Handle navigation to comparison view with dataset filtering
  const {navigateToComparison} = useLeaderboardNavigation(
    entity,
    project,
    selectedEvaluations,
    availableDatasets,
    allRecords
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      flexGrow={1}
      overflow="hidden">
      <CompareEvaluationsControlBar
        entity={entity}
        project={project}
        selectedEvaluations={selectedEvaluations}
        selectedModelsCount={selectedModelsCount}
        availableDatasets={availableDatasets}
        loading={loading}
        onClearSelection={() => setSelectedEvaluations([])}
        onDatasetSelect={navigateToComparison}
      />
      <Box
        display="flex"
        flexDirection="row"
        height="100%"
        flexGrow={1}
        overflow="hidden">
        <LeaderboardGrid
          entity={entity}
          project={project}
          loading={loading}
          data={data}
          allRecords={allRecords}
          selectedEvaluations={selectedEvaluations}
          onSelectedEvaluationsChange={setSelectedEvaluations}
        />
      </Box>
    </Box>
  );
};
